/**
 * OTA update decision — PURE helpers (no Angular, no Capacitor, no barrels).
 *
 * `UpdateService` fetches the manifest and the native/bundle versions and asks
 * these functions whether the manifest may be applied and how. Kept free of
 * runtime imports other than the concrete `compare-versions` file (never the
 * `@utils` barrel, which drags Angular) so it can be unit-tested directly.
 *
 * Every guard fails SAFE: whenever something cannot be verified, the manifest
 * is ignored and the currently running bundle is kept.
 */
import { compareVersions } from '../../../utils/src/lib/compare-versions';

/** Shape of the manifest JSON (`latest.json`) written by `scripts/ota-deploy.js`. */
export interface UpdateManifest {
  /** Semver version string, e.g. "1.2.3". */
  version: string;
  /** Public URL of the bundle zip. */
  url: string;
  /** Optional sha256 checksum for integrity verification. */
  checksum?: string;
  /**
   * Optional minimum NATIVE binary version (`App.getInfo().version`) able to run
   * this bundle. Absent → no native compatibility check (legacy manifests).
   */
  minNativeVersion?: string;
  // Every OTA is activated on the next launch (`pickBundleToActivate`); to force an
  // update, raise `minVersion`. Extra fields of older manifests are ignored.
}

/** Strict `major.minor.patch`: the only format the OTA pipeline produces. */
export const OTA_VERSION_PATTERN = /^\d+\.\d+\.\d+$/;

export type OtaSkipReason =
  | 'no-manifest'
  | 'invalid-manifest'
  | 'invalid-running-version'
  | 'not-newer'
  | 'already-active'
  | 'previously-failed'
  | 'invalid-min-native-version'
  | 'native-version-unknown'
  | 'native-too-old';

export type OtaApplicability = { applicable: true } | { applicable: false; reason: OtaSkipReason };

export interface OtaApplicabilityInput {
  /** Manifest as fetched (may be `null` or malformed). */
  manifest: UpdateManifest | null | undefined;
  /** Version of the running web bundle: `environment.liveVersion ?? environment.version`. */
  runningVersion: string | null | undefined;
  /**
   * Native binary version (`App.getInfo().version`), or `null` when it could not
   * be obtained. Only consulted when the manifest carries `minNativeVersion`.
   * Normalised with `normalizeNativeVersion` (`"1.0"` → `"1.0.0"`).
   */
  nativeVersion: string | null | undefined;
  /**
   * Optional: version capgo reports for the ACTIVE bundle (`current().bundle.version`).
   * Anti-loop safety net for bundles whose compiled `liveVersion` does not match
   * the manifest (e.g. not built by ota-deploy.js): never re-apply the active one.
   */
  activeBundleVersion?: string | null;
  /**
   * Optional: OTA versions that already failed to boot ON THIS DEVICE (see
   * `readFailedOtaVersions`). A manifest whose version is listed is never applied
   * again automatically nor manually — only a DIFFERENT version may enter.
   */
  failedVersions?: readonly string[] | null;
}

/** True when `v` is a strict `X.Y.Z` string. */
export function isOtaVersion(v: unknown): v is string {
  return typeof v === 'string' && OTA_VERSION_PATTERN.test(v);
}

/**
 * Normalises the NATIVE binary version before comparing it: Xcode's default
 * `MARKETING_VERSION` is `"1.0"` (seen on device with a Debug build), which the
 * strict X.Y.Z check would reject → `native-version-unknown` → OTA ignored.
 * `"1"` → `"1.0.0"`, `"1.0"` → `"1.0.0"`, `"1.2.3"` unchanged. Anything else
 * (non-numeric, suffixes, more than 3 parts, non-string) → `null` (unknown).
 */
export function normalizeNativeVersion(v: unknown): string | null {
  if (typeof v !== 'string' || !/^\d+(\.\d+){0,2}$/.test(v)) return null;
  const parts = v.split('.');
  while (parts.length < 3) parts.push('0');
  return parts.join('.');
}

/** True when the manifest declares a native compatibility floor (non-empty field present). */
export function requiresNativeCheck(manifest: UpdateManifest | null | undefined): boolean {
  return !!manifest && manifest.minNativeVersion !== undefined && manifest.minNativeVersion !== null;
}

/**
 * Decides whether a manifest may be downloaded/applied.
 *
 * 1. Manifest must exist with a strict semver `version` and a non-empty `url`.
 * 2. Never downgrade nor re-apply: `manifest.version` must be STRICTLY greater
 *    than the running bundle version (which must itself be valid), and must not
 *    be the bundle capgo reports as active.
 * 2b. Never retry a version that already failed to boot on this device
 *    (`failedVersions`): prevents a `set` + `reload` loop after capgo
 *    rolls back a bundle that never called `notifyAppReady`.
 * 3. Native compatibility: if `minNativeVersion` is present it must be valid,
 *    the native version must be known and valid (after `normalizeNativeVersion`),
 *    and native >= minNativeVersion.
 */
export function evaluateOtaManifest(input: OtaApplicabilityInput): OtaApplicability {
  const { manifest, runningVersion, nativeVersion, activeBundleVersion, failedVersions } = input;

  if (!manifest) return { applicable: false, reason: 'no-manifest' };
  if (!isOtaVersion(manifest.version) || typeof manifest.url !== 'string' || !manifest.url) {
    return { applicable: false, reason: 'invalid-manifest' };
  }

  if (!isOtaVersion(runningVersion)) return { applicable: false, reason: 'invalid-running-version' };
  if (compareVersions(manifest.version, runningVersion) <= 0) {
    return { applicable: false, reason: 'not-newer' };
  }
  if (activeBundleVersion === manifest.version) {
    return { applicable: false, reason: 'already-active' };
  }
  if (failedVersions?.includes(manifest.version)) {
    return { applicable: false, reason: 'previously-failed' };
  }

  if (requiresNativeCheck(manifest)) {
    if (!isOtaVersion(manifest.minNativeVersion)) {
      return { applicable: false, reason: 'invalid-min-native-version' };
    }
    const native = normalizeNativeVersion(nativeVersion);
    if (!isOtaVersion(native)) return { applicable: false, reason: 'native-version-unknown' };
    if (compareVersions(native, manifest.minNativeVersion) < 0) {
      return { applicable: false, reason: 'native-too-old' };
    }
  }

  return { applicable: true };
}

/** Minimal bundle shape returned by capgo (`BundleInfo`). */
export interface OtaBundleRef {
  id: string;
  version: string;
  status?: string;
}

/**
 * Statuses of a bundle that is fully downloaded and may be activated: `pending`
 * (set right after the download, and by `set`) and `success` (it booted and
 * called `notifyAppReady`). `error`, `deleted`, `deleting` and `downloading` never count.
 */
export const ACTIVATABLE_BUNDLE_STATUSES: readonly string[] = ['pending', 'success'];

/**
 * Returns the downloaded bundle of `version` from `CapacitorUpdater.list()`
 * (`{ bundles: BundleInfo[] }`, or the bare array), or `null`. Ignores the
 * `builtin` pseudo-bundle, entries without a string id, and any status outside
 * `ACTIVATABLE_BUNDLE_STATUSES`. Prefers a `success` entry, then the first
 * `pending` one. Never throws.
 *
 * `checksum` (optional, the manifest's): when both it and the stored bundle
 * checksum are non-empty they must be equal, otherwise the entry is skipped (and
 * the bundle downloaded again). capgo stores the bundle as `pending` BEFORE the
 * plugin compares the checksum and deletes a mismatching bundle, so a process
 * killed in that gap could leave a bad bundle listed as `pending`.
 */
export function findDownloadedBundle(listResult: unknown, version: unknown, checksum?: unknown): OtaBundleRef | null {
  if (!isOtaVersion(version)) return null;
  const raw = Array.isArray(listResult)
    ? listResult
    : listResult && typeof listResult === 'object'
      ? (listResult as { bundles?: unknown }).bundles
      : null;
  if (!Array.isArray(raw)) return null;

  const matches: OtaBundleRef[] = [];
  for (const entry of raw) {
    const ref = toBundleRef(entry);
    if (!ref || ref.id === BUILTIN_BUNDLE_ID || ref.version !== version) continue;
    if (!ACTIVATABLE_BUNDLE_STATUSES.includes(ref.status ?? '')) continue;
    const stored = (entry as { checksum?: unknown }).checksum;
    if (typeof checksum === 'string' && checksum && typeof stored === 'string' && stored && stored !== checksum) continue;
    matches.push(ref);
  }
  return matches.find((b) => b.status === 'success') ?? matches[0] ?? null;
}

/** Id capgo uses for the bundle shipped inside the native binary. */
export const BUILTIN_BUNDLE_ID = 'builtin';

/**
 * Picks the downloaded bundle `main.ts` activates before bootstrapping Angular:
 * the HIGHEST version in `CapacitorUpdater.list()` whose status is activatable
 * (`pending`/`success`; never `error`, `deleted`, `deleting`, `downloading`),
 * not builtin nor the active bundle, and strictly greater than BOTH the running
 * (compiled) version and capgo's version of the active bundle (`current`, the
 * result of `CapacitorUpdater.current()`; ignored for builtin). Using the maximum
 * avoids a ping-pong reload loop between two bundles when one was stamped with a
 * `liveVersion` lower than its capgo version. `null` when nothing qualifies or the
 * input is unreadable. Never throws.
 */
export function pickBundleToActivate(listResult: unknown, runningVersion: unknown, current?: unknown): OtaBundleRef | null {
  const raw = listResult && typeof listResult === 'object' ? (listResult as { bundles?: unknown }).bundles : null;
  if (!isOtaVersion(runningVersion) || !Array.isArray(raw)) return null;
  const active = toBundleRef((current as { bundle?: unknown } | null)?.bundle);
  const floor =
    active && active.id !== BUILTIN_BUNDLE_ID && isOtaVersion(active.version) && compareVersions(active.version, runningVersion) > 0
      ? active.version
      : runningVersion;
  let best: OtaBundleRef | null = null;
  for (const entry of raw) {
    const ref = toBundleRef(entry);
    if (!ref || ref.id === BUILTIN_BUNDLE_ID || ref.id === active?.id || !isOtaVersion(ref.version)) continue;
    if (!ACTIVATABLE_BUNDLE_STATUSES.includes(ref.status ?? '')) continue;
    if (compareVersions(ref.version, best?.version ?? floor) > 0) best = ref;
  }
  return best;
}

/**
 * What `main.ts` does on launch with capgo's `next` (`getNextBundle()`), which
 * bundles 0.0.23-0.0.27 left pointing at another bundle and capgo installs by
 * itself when the app goes to the background (hot reload → iOS hang):
 * - a bundle to activate → `pin` = `activate` (`next()` right before `set()`);
 * - nothing to activate but `next` differs from the active bundle → `pin` = active id;
 * - otherwise nothing.
 * `installNext` is inert when `next` is the active bundle (iOS compares version
 * names, Android ids; builtin included).
 */
export function planLaunchOta(
  listResult: unknown,
  runningVersion: unknown,
  current: unknown,
  next: unknown,
): { activate: string | null; pin: string | null } {
  const target = pickBundleToActivate(listResult, runningVersion, current);
  if (target) return { activate: target.id, pin: target.id };
  const activeId = toBundleRef((current as { bundle?: unknown } | null)?.bundle)?.id;
  const nextId = toBundleRef(next)?.id;
  return { activate: null, pin: activeId && nextId && nextId !== activeId ? activeId : null };
}

function toBundleRef(entry: unknown): OtaBundleRef | null {
  if (!entry || typeof entry !== 'object') return null;
  const { id, version, status } = entry as { id?: unknown; version?: unknown; status?: unknown };
  if (typeof id !== 'string' || !id) return null;
  return {
    id,
    version: typeof version === 'string' ? version : '',
    ...(typeof status === 'string' ? { status } : {}),
  };
}

// ─── Manifest fetch (native HTTP, no CORS) ─────────────────────────────────

/** Query parameter appended to the manifest URL to defeat intermediate caches. */
export const MANIFEST_CACHE_BUST_PARAM = 't';

/**
 * Timeout (ms) for the native manifest request, passed as both `connectTimeout`
 * and `readTimeout` to `CapacitorHttp.get`. Without it iOS falls back to 600 s,
 * leaving `/version` spinning for up to 10 minutes on a stalled network.
 * Android applies each to `HttpURLConnection`; iOS uses `connectTimeout` as the
 * `URLRequest.timeoutInterval`, which is an idle timeout, not a total cap.
 */
export const MANIFEST_REQUEST_TIMEOUT_MS = 10000;

/**
 * Returns `url` with `?t=<now>` (or `&t=<now>`) so no intermediate cache serves a
 * stale `latest.json`. GCS ignores unknown query parameters on public objects.
 * A request header (`Cache-Control`) is deliberately NOT used. Keeps any existing
 * query/hash; an unparseable URL → `null` (the manifest is then not fetched).
 */
export function manifestRequestUrl(url: unknown, now: number): string | null {
  if (typeof url !== 'string' || !url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    parsed.searchParams.set(MANIFEST_CACHE_BUST_PARAM, String(now));
    return parsed.toString();
  } catch {
    return null;
  }
}

/**
 * Validates the response of `CapacitorHttp.get` for `latest.json` (`{ status, data }`).
 * Accepts only status 200 with `data` being a plain object — or a JSON string
 * that parses to one (served without a JSON content type) — whose `version` and
 * `url` are strings and whose optional `checksum` is a string. Anything else →
 * `null`. Format checks (strict X.Y.Z, native floor…) stay in `evaluateOtaManifest`.
 */
export function parseManifestResponse(response: unknown): UpdateManifest | null {
  if (!response || typeof response !== 'object') return null;
  const { status, data } = response as { status?: unknown; data?: unknown };
  if (status !== 200) return null;

  let body: unknown = data;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return null;
    }
  }
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;

  const manifest = body as Record<string, unknown>;
  if (typeof manifest['version'] !== 'string' || typeof manifest['url'] !== 'string') return null;
  if (manifest['checksum'] !== undefined && typeof manifest['checksum'] !== 'string') return null;
  return manifest as unknown as UpdateManifest;
}

// ─── Failed OTA versions (device-local memory) ─────────────────────────────

/**
 * localStorage key holding the OTA versions that failed to boot on this device.
 * The WebView origin does not change between capgo bundles, so the value
 * survives the rollback to the builtin / previous bundle.
 */
export const FAILED_OTA_VERSIONS_KEY = 'dms.ota.failedVersions';

/** Only the most recent N failed versions are kept (older ones are dropped). */
export const FAILED_OTA_VERSIONS_LIMIT = 10;

/** Minimal storage contract (structurally compatible with `Storage` / `safeLocalStorage()`). */
export interface OtaFailureStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Parses the persisted list: keeps only strict X.Y.Z strings, de-duplicated,
 * capped to the last `FAILED_OTA_VERSIONS_LIMIT`. Anything unreadable → `[]`.
 */
export function parseFailedOtaVersions(raw: string | null | undefined): string[] {
  if (typeof raw !== 'string' || !raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const unique: string[] = [];
  for (const v of parsed) {
    if (!isOtaVersion(v)) continue;
    const idx = unique.indexOf(v);
    if (idx !== -1) unique.splice(idx, 1);
    unique.push(v);
  }
  return unique.slice(-FAILED_OTA_VERSIONS_LIMIT);
}

/**
 * Returns a new list with `version` appended as the most recent entry (moved to
 * the end if already present), capped to `FAILED_OTA_VERSIONS_LIMIT`. Invalid
 * versions (e.g. `builtin`) leave the list unchanged.
 */
export function addFailedOtaVersion(list: readonly string[], version: unknown): string[] {
  const current = list.filter(isOtaVersion);
  if (!isOtaVersion(version)) return current.slice(-FAILED_OTA_VERSIONS_LIMIT);
  return [...current.filter((v) => v !== version), version].slice(-FAILED_OTA_VERSIONS_LIMIT);
}

/** Reads the failed versions; never throws (unavailable/corrupt storage → `[]`). */
export function readFailedOtaVersions(storage: OtaFailureStorage | null | undefined): string[] {
  if (!storage) return [];
  try {
    return parseFailedOtaVersions(storage.getItem(FAILED_OTA_VERSIONS_KEY));
  } catch {
    return [];
  }
}

/**
 * Persists `version` as failed. Never throws. Returns the resulting list (what
 * callers must use for the current decision even if the write itself failed).
 */
export function rememberFailedOtaVersion(storage: OtaFailureStorage | null | undefined, version: unknown): string[] {
  const next = addFailedOtaVersion(readFailedOtaVersions(storage), version);
  if (!storage || !isOtaVersion(version)) return next;
  try {
    storage.setItem(FAILED_OTA_VERSIONS_KEY, JSON.stringify(next));
  } catch {
    // Quota / disabled storage: the in-memory list still protects this runtime.
  }
  return next;
}

/**
 * Extracts the failed bundle version from `CapacitorUpdater.getFailedUpdate()` /
 * the `updateFailed` event payload (`{ bundle: BundleInfo }`, `null`, or `{}` on
 * iOS when nothing was recorded). Returns `null` unless it is a strict X.Y.Z.
 */
export function failedVersionFromEvent(event: unknown): string | null {
  if (!event || typeof event !== 'object') return null;
  const bundle = (event as { bundle?: unknown }).bundle;
  if (!bundle || typeof bundle !== 'object') return null;
  const version = (bundle as { version?: unknown }).version;
  return isOtaVersion(version) ? version : null;
}

/**
 * Single-flight by key: while a task for `key` is in flight, further `run(key, …)`
 * calls get the same promise (their task is not executed). Once it settles the
 * key is free again. Used so the background check and the `/version` button never
 * download the same version twice at the same time.
 */
export function createKeyedSingleFlight<T>(): { run(key: string, task: () => Promise<T>): Promise<T> } {
  const inFlight = new Map<string, Promise<T>>();
  return {
    run(key, task) {
      const existing = inFlight.get(key);
      if (existing) return existing;
      const promise = Promise.resolve()
        .then(task)
        .finally(() => inFlight.delete(key));
      inFlight.set(key, promise);
      return promise;
    },
  };
}

/**
 * Busy counter behind `downloading$`: `begin()` / `end()` may overlap (a
 * background check and the `/version` button); `busy` stays `true` until the
 * LAST operation ends. `end()` never goes below 0.
 */
export function createBusyCounter(): { begin(): boolean; end(): boolean; busy(): boolean } {
  let count = 0;
  return {
    begin: () => {
      count += 1;
      return count > 0;
    },
    end: () => {
      count = Math.max(0, count - 1);
      return count > 0;
    },
    busy: () => count > 0,
  };
}
